import React, { useMemo, useEffect, useState, useCallback } from 'react';

import { useWindowSize } from '../hooks/use-window-size';

import { formatDate, formatCurrency } from '../lib/formatters';

import { Subscribe } from 'unstated';

import { WineHistoryContainer } from './containers/wine-history-container';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import _ from 'lodash';

import ReactImageFallback from 'react-image-fallback';
import ProgressiveImage from 'react-progressive-image';

import winePlaceholder from '../assets/images/wine-placeholder.png';
import seeIcon from '../assets/images/tasting_notes/1.png';
import smellIcon from '../assets/images/tasting_notes/2.png';
import sipIcon from '../assets/images/tasting_notes/3.png';
import savorIcon from '../assets/images/tasting_notes/4.png';
import serveIcon from '../assets/images/tasting_notes/5.png';

const NINETY_ONE_DAYS_AGO = new Date(new Date() - 91 * 86400 * 1000);

const getRatingButtonClass = (forRating, isActive, pendingRating) => {
  if (pendingRating) {
    return forRating === pendingRating ? 'active is-loading' : 'disabled';
  }
  return isActive ? 'active' : '';
};

const BaseRatingControls = ({
  rateWine,
  wineImageUrl,
  like,
  favorite,
  dislike,
  notes,
  description,
  children,
  receivedOn,
  showTitleTop,
  pendingRating
}) => (
  <>
    <div style={{ display: 'contents' }}>
      {showTitleTop && [
        <div key="1">{description}</div>,
        receivedOn && (
          <div key="2" className="font-italic text-black-50 ">
            <small>Received on {formatDate(receivedOn)}</small>
          </div>
        )
      ]}
      <img
        src={wineImageUrl || winePlaceholder}
        loading="lazy"
        onError={(e) => (e.target.src = winePlaceholder)}
        className="mt-3 img-fluid rounded img-square"
        alt={description}
      />
      {!showTitleTop && [
        <div key="3">{description}</div>,
        receivedOn && (
          <div key="4" className="font-italic text-black-50 ">
            <small>Received on {formatDate(receivedOn)}</small>
          </div>
        )
      ]}
    </div>

    <div className="btn-container">
      <div
        className="btn-group btn-group-toggle mt-3"
        role="group"
        aria-label="Ratings"
      >
        <button
          onClick={(e) => rateWine('loved', notes) && e.target.blur()}
          type="button"
          className={`btn btn-tertiary rating-btn ${getRatingButtonClass('loved', favorite, pendingRating)}`}
        >
          <FontAwesomeIcon icon={['fas', 'heart']} />
        </button>

        <button
          onClick={(e) => rateWine('liked', notes) && e.target.blur()}
          type="button"
          className={`btn btn-tertiary rating-btn ml-2 mr-2 ${getRatingButtonClass('liked', like, pendingRating)}`}
        >
          <FontAwesomeIcon icon={['far', 'thumbs-up']} />
        </button>

        <button
          onClick={(e) => rateWine('disliked', notes) && e.target.blur()}
          type="button"
          className={`btn btn-tertiary rating-btn ${getRatingButtonClass('disliked', dislike, pendingRating)}`}
        >
          <FontAwesomeIcon icon={['far', 'thumbs-down']} />
        </button>
      </div>
      {children}
    </div>
  </>
);

const TastingHelp = ({}) => (
  <>
    {' '}
    <h5 className="modal-title mb-3">Rating Information</h5>
    <div className="d-flex mb-3">
      <div className="dc-static-icon">
        <FontAwesomeIcon icon={['fas', 'heart']} />
      </div>
      <div className="font-primary">
        I love this wine and want to receive it again sometime.
      </div>
    </div>
    <div className="d-flex mb-3">
      <div className="dc-static-icon">
        <FontAwesomeIcon icon={['far', 'thumbs-up']} />
      </div>
      <div className="font-primary">
        I like this wine, but I want to try something new
      </div>
    </div>
    <div className="d-flex">
      <div className="dc-static-icon">
        <FontAwesomeIcon icon={['far', 'thumbs-down']} />
      </div>
      <div className="font-primary">I did not like this wine.</div>
    </div>
  </>
);

const TastingNotesIcon = ({ src }) => (
  <img src={src} className="dc-notes_icon" />
);

const WineCard = ({ product, saveRating }) => {
  const [modalState, setModalState] = useState(undefined);
  const [rating, setRating] = useState(product.rating || {});
  const [pendingRating, setPendingRating] = useState(undefined);
  const [showHelpText, setShowHelpText] = useState(false);
  const description = product.name;

  const rateWine = useCallback(
    async (rating, notes) => {
      setPendingRating(rating);
      try {
        const newRating = await saveRating(product.id, rating, notes);
        setRating(newRating);
      } finally {
        setPendingRating(undefined);
      }
    },
    [product, saveRating]
  );

  const { loved: favorite, score, notes } = rating;
  const [enteredNotes, setEnteredNotes] = useState(notes || '');

  const like = score === 1 && !favorite;
  const dislike = score === -1;
  const currentRating = favorite
    ? 'loved'
    : like
      ? 'liked'
      : (dislike && 'disliked') || 'unrated';

  const wineDetail = product.wineDetails;
  const wineImageUrl = wineDetail ? wineDetail.conciergeUrl : undefined;

  const disableNotes = !(like || dislike || favorite);
  const receivedOn = product.lastDelivery || product.lastPurchased;

  return (
    <div
      className="col-sm-4 bg-white"
      style={{ minHeight: '650px', justifyContent: 'space-between' }}
    >
      <BaseRatingControls
        rateWine={rateWine}
        wineImageUrl={wineImageUrl}
        like={like}
        dislike={dislike}
        favorite={favorite}
        notes={enteredNotes}
        description={description}
        pendingRating={pendingRating}
      >
        <div>
          <a
            className="btn btn-primary btn-xl mt-3"
            onClick={() => setModalState('feedBack')}
          >
            ADDITIONAL FEEDBACK
          </a>
        </div>
        <div>
          <a
            className="btn btn-secondary btn-xl mt-3"
            onClick={() => setModalState('tastingNotes')}
          >
            TASTING NOTES
          </a>
        </div>
      </BaseRatingControls>

      <div
        className="modal"
        style={{
          display: !_.isNil(modalState) ? 'block' : 'none'
        }}
        tabIndex="-1"
        role="dialog"
        aria-hidden="true"
        data-show={!_.isNil(modalState) ? 'true' : 'false'}
      >
        <div className="modal-dialog" role="document">
          <div className="modal-content font-primary">
            {modalState === 'feedBack' && (
              <>
                <div className="modal-header">
                  <h5
                    className="modal-title text-center"
                    id="feedbackLabel"
                    style={{ cursor: 'pointer' }}
                  >
                    Rate This Wine
                    <a
                      href="javascript:void(0);"
                      onClick={() => setShowHelpText(!showHelpText)}
                      className="dc-help-text"
                    >
                      ?
                      <div className="dc-help-text_content d-none d-md-block">
                        <TastingHelp />
                      </div>
                    </a>
                  </h5>

                  {showHelpText && (
                    <div className="dc-help-text_content dc-help-text_mobile d-md-none">
                      <TastingHelp />
                    </div>
                  )}

                  <button
                    type="button"
                    className="close"
                    data-dismiss="modal"
                    aria-label="Close"
                    onClick={() => setModalState(undefined)}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body text-center">
                  <div className="bg-white center-block">
                    <BaseRatingControls
                      rateWine={rateWine}
                      wineImageUrl={wineImageUrl}
                      like={like}
                      dislike={dislike}
                      favorite={favorite}
                      notes={enteredNotes}
                      description={description}
                      showTitleTop={true}
                      receivedOn={receivedOn}
                      pendingRating={pendingRating}
                    >
                      <div className="form-group input-group mb-0 mt-3">
                        <textarea
                          disabled={disableNotes}
                          rows="3"
                          maxLength="255"
                          className="dc-textarea"
                          onChange={(e) => setEnteredNotes(e.target.value)}
                          defaultValue={_.trim(enteredNotes)}
                        />
                      </div>
                      <div className="font-italic text-black-50 mb-2">
                        <small>
                          Characters left: {255 - enteredNotes.length}
                        </small>
                      </div>
                      {disableNotes ? (
                        <h5>Please rate this wine before commenting</h5>
                      ) : undefined}
                    </BaseRatingControls>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    disabled={disableNotes}
                    className="btn btn-primary btn-xl"
                    onClick={() =>
                      rateWine(currentRating, enteredNotes).then(() =>
                        setModalState(undefined)
                      )
                    }
                  >
                    Save
                  </button>
                  <button
                    className="btn btn-primary btn-xl"
                    onClick={() => setModalState('tastingNotes')}
                  >
                    Tasting Notes
                  </button>
                </div>
              </>
            )}

            {modalState === 'tastingNotes' && (
              <>
                <div className="modal-header">
                  <h5 className="modal-title text-center">Tasting Notes</h5>

                  <button
                    type="button"
                    className="close"
                    data-dismiss="modal"
                    aria-label="Close"
                    onClick={() => setModalState(undefined)}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
                <div className="modal-body text-center">
                  {receivedOn && (
                    <div key="2" className="font-italic text-black-50 mb-3">
                      <small>Received on {formatDate(receivedOn)}</small>
                    </div>
                  )}

                  <div className="row">
                    <div className="col-sm-4">
                      <ProgressiveImage
                        src={wineImageUrl}
                        placeholder={winePlaceholder}
                      >
                        {(src, loading) => (
                          <ReactImageFallback
                            src={src}
                            style={{ opacity: loading ? 0.5 : 1 }}
                            className="img-fluid rounded p-0 img-square"
                            fallbackImage={winePlaceholder}
                            alt={description}
                          />
                        )}
                      </ProgressiveImage>
                    </div>
                    <div className="col-sm-8 text-left">
                      <div className="d-none d-sm-block">
                        {wineDetail && wineDetail.description}
                      </div>
                      <div className="d-sm-none text-center">
                        {wineDetail && wineDetail.description}
                      </div>
                      <div className="d-none d-sm-block">
                        {wineDetail && formatCurrency(wineDetail.price)}
                      </div>
                      <div className="d-sm-none text-center">
                        {wineDetail && formatCurrency(wineDetail.price)}
                      </div>
                      <div className="my-3 p-3 dc-tile">
                        <div>
                          <strong>Varietal:</strong>{' '}
                          {wineDetail && wineDetail.varietal}
                        </div>
                        <div>
                          <strong>Region:</strong>{' '}
                          {wineDetail && wineDetail.region}
                        </div>
                        <div>
                          {wineDetail && _.toNumber(wineDetail.year) > 0 && (
                            <span>
                              <strong>Vintage:</strong> {wineDetail.year}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                  <h5 className="mb-3 mt-4">Tasting Tips</h5>
                  <div className="text-left dc-tasting-tip_mobile">
                    <div className="d-flex mb-3">
                      <div className="mr-2">
                        <TastingNotesIcon src={seeIcon} />
                      </div>
                      <div>
                        <strong>SEE:</strong> {wineDetail && wineDetail.see}
                      </div>
                    </div>
                    <div className="d-flex mb-3">
                      <div className="mr-2">
                        <TastingNotesIcon src={smellIcon} />
                      </div>
                      <div>
                        <strong>SMELL:</strong> {wineDetail && wineDetail.smell}
                      </div>
                    </div>
                    <div className="d-flex mb-3">
                      <div className="mr-2">
                        <TastingNotesIcon src={sipIcon} />
                      </div>
                      <div>
                        <strong>SIP:</strong> {wineDetail && wineDetail.sip}
                      </div>
                    </div>
                    <div className="d-flex mb-3">
                      <div className="mr-2">
                        <TastingNotesIcon src={savorIcon} />
                      </div>
                      <div>
                        <strong>SAVOR:</strong> {wineDetail && wineDetail.savor}
                      </div>
                    </div>
                    <div className="d-flex">
                      <div className="mr-2">
                        <TastingNotesIcon src={serveIcon} />
                      </div>
                      <div>
                        <strong>SERVE:</strong> Serving Temperature:{' '}
                        {wineDetail && wineDetail.servingTemp}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    className="btn btn-primary btn-xl"
                    onClick={() => setModalState('feedBack')}
                  >
                    RATE THIS WINE
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div
        className="modal-backdrop fade show"
        style={{
          display: !_.isNil(modalState) ? 'block' : 'none'
        }}
      />
    </div>
  );
};

const WinesView = ({ wineHistoryContainer }) => {
  let wineNameInput;
  const [salesHistory, setSalesHistory] = useState();
  const [loading, setLoading] = useState(true);
  const [searchPattern, _setSearchPattern] = useState('');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filterRating, setFilterRating] = useState(undefined);
  const [filterDeliveryDate, setDeliveryDateFilter] = useState(undefined);
  const [trigger, setTrigger] = useState(0);

  const setSearchPattern = _.debounce(_setSearchPattern, 150);
  useEffect(
    () => _.tap(window.$, ($) => $(() => $('.carousel').carousel({}))),
    []
  );

  useEffect(() => {
    let mounted = true;
    wineHistoryContainer
      .all((productHistory) => mounted && setSalesHistory(productHistory))
      .then(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const getFilterMenuItem = (
    currentValue,
    filterValue,
    filter,
    formatter = _.capitalize
  ) =>
    currentValue === filterValue ? (
      <a
        key={filterValue}
        className="dropdown-item bg-info text-white justify-content-between d-inline-flex align-items-center"
        onClick={() => filter(undefined)}
      >
        <span className="mx-3">{formatter(filterValue)}</span>
        <FontAwesomeIcon icon={['fa', 'times']} />
      </a>
    ) : (
      <a
        key={filterValue}
        className="dropdown-item"
        onClick={() => filter(filterValue)}
      >
        <span className="mx-3">{formatter(filterValue)}</span>
      </a>
    );

  const deliveryDates = useMemo(() =>
    _.uniq(
      (salesHistory || [])
        .filter(({ lastDelivery }) => lastDelivery)
        .map(({ lastDelivery, lastPurchased }) => {
          const delivery = lastDelivery.split('T')[0];
          const purchased = lastPurchased.split('T')[0];
          return delivery !== purchased ? delivery : undefined;
        })
        .filter((v) => !_.isEmpty(v) && new Date(v) > NINETY_ONE_DAYS_AGO)
    )
  );
  useEffect(() => {
    if (!deliveryDates.includes(filterDeliveryDate)) {
      setDeliveryDateFilter(undefined);
    }
  }, [deliveryDates, filterDeliveryDate]);

  const saveRating = useCallback(async (productId, rating, notes) => {
    const newRating = await wineHistoryContainer.saveRating(
      productId,
      rating,
      notes
    );
    setTrigger(Math.random());
    return newRating;
  }, []);

  const filteredSalesHistory = useMemo(
    () =>
      (salesHistory || [])
        .filter(
          ({ name }) =>
            !searchPattern ||
            name.toLowerCase().includes(searchPattern.toLowerCase())
        )
        .filter(
          ({ rating }) =>
            !filterRating ||
            (filterRating === 'favorite' && _.get(rating, 'loved')) ||
            (filterRating === 'liked' && _.get(rating, 'score') === 1) ||
            (filterRating === 'disliked' && _.get(rating, 'score') === -1) ||
            (filterRating === 'unrated' && !rating)
        )
        .filter(
          ({ lastDelivery }) =>
            !filterDeliveryDate ||
            (lastDelivery && lastDelivery.startsWith(filterDeliveryDate))
        )
        .sort(
          (l, r) =>
            (sortDirection === 'asc' ? 1 : -1) *
            (new Date(l.lastDelivery || l.lastPurchased) -
              new Date(r.lastDelivery || r.lastPurchased))
        ),
    [
      salesHistory,
      searchPattern,
      filterRating,
      filterDeliveryDate,
      sortDirection,
      trigger
    ]
  );

  return (
    <>
      <section className="page-section clearfix">
        <div className="container">
          <div className="row">
            <div className="col-sm-12">
              <h4 className="text-center mb-4">My Wines</h4>
              <hr />

              <div className="input-group pt-3 dc-search-filter">
                <div className="input-group-prepend">
                  <div className="dropdown">
                    <button
                      type="button"
                      className="btn btn-outline-secondary dropdown-toggle"
                      data-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      <span className="font-weight-semibold">Filter </span>
                    </button>
                    <div className="dropdown-menu">
                      <h5 className="dropdown-header font-weight-bolder">
                        Rating
                      </h5>
                      {['favorite', 'liked', 'disliked', 'unrated'].map(
                        (value) =>
                          getFilterMenuItem(
                            filterRating,
                            value,
                            setFilterRating
                          )
                      )}
                      {!_.isEmpty(deliveryDates) && (
                        <>
                          <h5 className="dropdown-header font-weight-bolder">
                            Received on
                          </h5>
                          {deliveryDates.map((value) =>
                            getFilterMenuItem(
                              filterDeliveryDate,
                              value,
                              setDeliveryDateFilter,
                              formatDate
                            )
                          )}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="dropdown">
                    <button
                      type="button"
                      className="btn btn-outline-secondary dropdown-toggle"
                      data-toggle="dropdown"
                      aria-haspopup="true"
                      aria-expanded="false"
                    >
                      <span className="font-weight-semibold">Sort </span>
                    </button>
                    <div className="dropdown-menu">
                      <h5 className="dropdown-header font-weight-bolder">
                        Date Received
                      </h5>
                      <div
                        className={`dropdown-item ${sortDirection === 'desc' ? 'bg-info text-white' : ''}`}
                        onClick={() => setSortDirection('desc')}
                      >
                        <span className="mx-3">Descending</span>
                      </div>
                      <div
                        className={`dropdown-item ${sortDirection === 'asc' ? 'bg-info text-white' : ''}`}
                        onClick={() => setSortDirection('asc')}
                      >
                        <span className="mx-3">Ascending</span>
                      </div>
                    </div>
                  </div>
                </div>
                <input
                  type="text"
                  placeholder="Wine name"
                  className="form-control"
                  aria-label="Text input with segmented dropdown button"
                  onChange={(e) => setSearchPattern(e.target.value)}
                  // (e) => {
                  //   e.persist();
                  //   onWineNameChangeEvent(e);
                  // }}
                  // onKeyDown={(e) => {
                  //   if (e.key === 'Enter') {
                  //     e.preventDefault();
                  //     e.stopPropagation();
                  //
                  //     filterByWineName(_.trim(wineNameInput.value));
                  //   }
                  // }}
                  ref={(i) => (wineNameInput = i)}
                />
                <div className="input-group-append">
                  <FontAwesomeIcon icon={['fas', 'search']} />
                </div>
              </div>
              <div className="p-3">
                {filterRating && (
                  <span className="badge badge-pill badge-info d-inline-flex align-items-center mx-1">
                    Rating: {_.capitalize(filterRating)}
                    <button
                      type="button"
                      className="btn btn-link p-0 ml-1"
                      aria-label="Remove"
                      style={{
                        lineHeight: 1,
                        textDecoration: 'none',
                        color: 'inherit',
                        marginTop: '2px'
                      }}
                      onClick={() => setFilterRating(undefined)}
                    >
                      <FontAwesomeIcon icon={['fa', 'times']} />
                    </button>
                  </span>
                )}
                {filterDeliveryDate && (
                  <span className="badge badge-pill badge-info d-inline-flex align-items-center mx-1">
                    Received on: {formatDate(filterDeliveryDate)}
                    <button
                      type="button"
                      className="btn btn-link p-0 ml-1"
                      aria-label="Remove"
                      style={{
                        lineHeight: 1,
                        textDecoration: 'none',
                        color: 'inherit',
                        marginTop: '2px'
                      }}
                      onClick={() => setDeliveryDateFilter(undefined)}
                    >
                      <FontAwesomeIcon icon={['fa', 'times']} />
                    </button>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="page-section clearfix pt-5 pb-5 m-0" id="mywines">
        <div className="container d-lg-none d-lg-block">
          {filteredSalesHistory.map((product) => (
            <WineCard
              key={product.id}
              product={product}
              saveRating={saveRating}
            />
          ))}
          {!loading && _.isEmpty(salesHistory) && (
            <p className="text-center">
              You'll be able to review wines here after you receive them to tell
              us what you think!
            </p>
          )}
          {loading && (
            <div
              className="w-auto h-auto bg-white text-center align-content-center"
              style={{ minHeight: '350px', justifyContent: 'space-between' }}
            >
              <span
                key={1}
                className="spinner-grow spinner-grow-sm"
                role="status"
                aria-hidden="true"
              />
            </div>
          )}
        </div>
      </section>
    </>
  );
};

const Wines = () => (
  <Subscribe to={[WineHistoryContainer]}>
    {(wineHistoryContainer) => (
      <WinesView wineHistoryContainer={wineHistoryContainer} />
    )}
  </Subscribe>
);

export default Wines;
